import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useTopology from '@api/hooks/topology/useTopology';
import { getIsLoggedIn } from '@api/selectors/auth';
import Header from '@cont/Compete/Landing/Header/Header';
import { TAB_INDICES } from '@cont/SeeAll/constants';
import styled from '@emotion/styled';
import segmentStorage from '@events/segmentStorage';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useScrollHandlerGroup from '@hooks/useScrollHandlerGroup';
import { mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { COMPETE_LANDING_PAGE_VIEWED } from '../constants';
import RatingProgress from '../RatingProgress/RatingProgress';
import FAQs from './FAQs';
import HowCompeteWorksCard from './HowCompeteWorksCard';
import LeaderboardWithFilters from './Leaderboard/LeaderboardWithFilters';
import RatingBadgeCard from './RatingBadgeCard';
import RatingsAndLevels from './RatingsAndLevels';
import Syllabus from './Syllabus/Syllabus';

const Wrapper = styled.div`
  grid-column: span 18;
  margin-top: -${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const Landing = ({ fallback }) => {
  const ratingProgressRef = useRef();
  const leaderboardRef = useRef();
  const FAQRef = useRef();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [refsList, setRefsList] = useState([]);
  const { goalUID } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const { topology } = useTopology(goalUID);

  const refs = [
    {
      ref: ratingProgressRef,
      id: 'RATING_PROGRESS',
      shrinkPos: 600
    },
    {
      ref: leaderboardRef,
      id: 'LEADERBOARD',
      shrinkPos: 300
    },
    {
      ref: FAQRef,
      id: 'FAQ',
      shrinkPos: 600
    }
  ];

  const { activeStatuses, scrollToElement } = useScrollHandlerGroup(refsList, {
    offsetTop: 250
  });

  useEffect(() => {
    const lps = segmentStorage.getItem('lps');

    segment.track(COMPETE_LANDING_PAGE_VIEWED, {
      ...goalSegmentData,
      last_primary_source: lps ?? 'NA',
      goal_name: topology.name
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      return setRefsList(refs.slice(1));
    }
    return setRefsList(refs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isSectionOneActive, isSectionTwoActive, isSectionThreeActive] =
    activeStatuses;

  const getSelectedTab = useCallback(() => {
    let newSelectedTab = selectedTabIndex;
    if (isSectionOneActive) {
      newSelectedTab = TAB_INDICES.FIRST_INDEX;
    }
    if (isSectionTwoActive) {
      newSelectedTab = TAB_INDICES.SECOND_INDEX;
    } else if (isSectionThreeActive) {
      newSelectedTab = TAB_INDICES.THIRD_INDEX;
    }
    if (newSelectedTab === selectedTabIndex) return null;
    setSelectedTabIndex(newSelectedTab);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectionOneActive, isSectionTwoActive, isSectionThreeActive]);

  useEffect(() => {
    getSelectedTab();
  }, [getSelectedTab]);

  const scrollToRef = (ref, id) => {
    scrollToElement(ref.current, id);
  };

  const handleChangeInDesktop = (tabIndex) => {
    if (tabIndex === TAB_INDICES.FIRST_INDEX) {
      scrollToRef(refsList[0].ref, refsList[0].id);
    }
    if (tabIndex === TAB_INDICES.SECOND_INDEX) {
      scrollToRef(refsList[1].ref, refsList[1].id);
    }
    if (tabIndex === TAB_INDICES.THIRD_INDEX) {
      scrollToRef(refsList[2].ref, refsList[2].id);
    }
  };

  const handleChange = (tabIndex) => {
    handleChangeInDesktop(tabIndex);
    setSelectedTabIndex(tabIndex);
  };

  return (
    <Wrapper>
      <Header handleChange={handleChange} selectedTabIndex={selectedTabIndex} />
      {isLoggedIn && (
        <>
          <RatingBadgeCard />
          <RatingProgress ref={ratingProgressRef} />
        </>
      )}
      <LeaderboardWithFilters fallback={fallback} ref={leaderboardRef} />
      <Syllabus />
      <RatingsAndLevels />
      <HowCompeteWorksCard />
      <FAQs ref={FAQRef} />
    </Wrapper>
  );
};

export default Landing;
